import React from 'react'
import styled from 'styled-components'
import { Container, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { MainHeading, TextStyled } from '../../../ui/Elements'
import { FooterLogo, GooglePlay } from '../../../ui/AllImages'
import { APP_STORE_URL, PLAY_STORE_URL } from '../../../utils/appStoreRedirect'
import { QR_DOWNLOAD_URL, getQrImageSrc } from '../../../utils/qrDownload'

const openStore = (url) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(url, '_blank', 'noopener,noreferrer')
}

const AppDownloadSection = () => {
    const { t } = useTranslation()

    return (
        <DownloadSectionWrapper className="container-fluid common-space" id="download-app">
            <Container>
                <Row className="align-items-center justify-content-between g-5">
                    <Col lg={6} className="text-lg-start text-center">
                        <MainHeading className="mb-3 text-dark download-title">
                            {t('AppDownloadTitle')}
                        </MainHeading>
                        <TextStyled className="mb-4 text-muted max-w-500 download-subtitle">
                            {t('AppDownloadSubtitle')}
                        </TextStyled>
                        <ButtonsContainer className="d-flex flex-nowrap justify-content-lg-start justify-content-center">
                            <StoreButton
                                href={APP_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={openStore(APP_STORE_URL)}
                            >
                                <span className="store-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.07 2.47.3 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42.14-1.38 1.83zM15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.21.67-2.93 1.49-.62.69-1.16 1.84-1.01 2.96 1.12.09 2.27-.56 2.95-1.39z"/>
                                    </svg>
                                </span>
                                <div className="btn-text">
                                    <span className="top-text">Download on the</span>
                                    <span className="bottom-text">App Store</span>
                                </div>
                            </StoreButton>

                            <StoreButton
                                href={PLAY_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={openStore(PLAY_STORE_URL)}
                            >
                                <span className="store-icon" aria-hidden="true">
                                    <img src={GooglePlay} alt="" width="28" height="28" />
                                </span>
                                <div className="btn-text">
                                    <span className="top-text">GET IT ON</span>
                                    <span className="bottom-text">Google Play</span>
                                </div>
                            </StoreButton>
                        </ButtonsContainer>
                    </Col>

                    <Col lg={5} className="d-flex justify-content-center">
                        <QRCardWrapper>
                            <QRContainer>
                                <QRScannerFrame>
                                    <span className="corner top-left" />
                                    <span className="corner top-right" />
                                    <span className="corner bottom-left" />
                                    <span className="corner bottom-right" />
                                    <span className="scan-line" />

                                    <QRCodeImageWrapper>
                                        <img
                                            src={getQrImageSrc(QR_DOWNLOAD_URL, 600)}
                                            srcSet={`${getQrImageSrc(QR_DOWNLOAD_URL, 600)} 1x, ${getQrImageSrc(QR_DOWNLOAD_URL, 1000)} 2x`}
                                            alt="Download QR Code"
                                            loading="lazy"
                                            width={200}
                                            height={200}
                                        />
                                        <QRLogoOverlay>
                                            <img src={FooterLogo} alt="" />
                                        </QRLogoOverlay>
                                    </QRCodeImageWrapper>
                                </QRScannerFrame>
                            </QRContainer>

                            <QRTextBlock>
                                <QRText>{t('ScanToDownload')}</QRText>
                                <QRHint>{t('ScanToDownloadHint')}</QRHint>
                            </QRTextBlock>
                        </QRCardWrapper>
                    </Col>
                </Row>
            </Container>
        </DownloadSectionWrapper>
    )
}

const DownloadSectionWrapper = styled.section`
    position: relative;
    z-index: 5;
    background: var(--white);
    padding: 80px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    
    .max-w-500 {
        max-width: 500px;
        width: 100%;
    }

    .download-title,
    .download-subtitle {
        display: block;
        max-width: 500px;
        width: 100%;
    }

    .download-subtitle {
        margin-top: 0 !important;
    }

    /* Project uses body.rtl (not html[dir=rtl]) */
    body.rtl & {
        .text-lg-start,
        .text-center {
            text-align: right !important;
        }

        .download-title,
        .download-subtitle {
            direction: rtl;
            text-align: right !important;
            margin-left: 0;
            margin-right: 0;
        }

        .download-title {
            margin-bottom: 28px !important;
        }

        .download-subtitle {
            margin-top: 12px !important;
        }

        .justify-content-lg-start {
            justify-content: flex-end !important;
        }
    }
`

const ButtonsContainer = styled.div`
    margin-top: 30px;
    position: relative;
    z-index: 6;
    width: 100%;
    max-width: 500px;
    gap: 12px;
    flex-wrap: nowrap !important;

    body.rtl & {
        margin-left: 0;
        margin-right: 0;
    }

    @media (max-width: 575px) {
        gap: 8px;
        max-width: 100%;
    }
`

const StoreButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #111111;
    color: var(--white) !important;
    padding: 8px 14px;
    border-radius: 14px;
    text-decoration: none !important;
    min-width: 168px;
    height: 54px;
    box-sizing: border-box;
    transition: all 0.3s ease;
    border: 1px solid transparent;
    cursor: pointer;
    pointer-events: auto;
    position: relative;
    z-index: 7;
    direction: ltr;

    .store-icon {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        img, svg {
            width: 28px;
            height: 28px;
            display: block;
            object-fit: contain;
        }
    }

    .btn-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        text-align: left;
        flex: 0 0 auto;
        width: auto;
        max-width: 120px;
        pointer-events: none;
        direction: ltr;
    }

    .top-text {
        font-size: 10px;
        font-family: var(--regular) !important;
        text-transform: none;
        opacity: 0.85;
        display: block;
        line-height: 1.15;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        padding-top: 0 !important;
    }

    .bottom-text {
        font-size: 15px;
        font-weight: 700;
        font-family: var(--regular) !important;
        display: block;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        padding-top: 0 !important;
    }

    &:hover {
        background: #222222;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        color: var(--white) !important;
    }

    /* iPhone SE / small phones: one row, same English badges */
    @media (max-width: 575px) {
        flex: 1 1 0;
        min-width: 0;
        max-width: none;
        width: auto;
        height: 48px;
        padding: 6px 8px;
        gap: 6px;
        border-radius: 12px;
        justify-content: center;

        .store-icon {
            width: 22px;
            height: 22px;

            img, svg {
                width: 22px;
                height: 22px;
            }
        }

        .btn-text {
            max-width: none;
            min-width: 0;
            flex: 0 1 auto;
        }

        .top-text {
            font-size: 8.5px;
        }

        .bottom-text {
            font-size: 12px;
        }
    }
`

const QRCardWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 240px;
    padding: 18px 16px 16px;
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: var(--themeColor);
    border: none;
    box-shadow: 0 10px 28px rgba(255, 131, 25, 0.35);
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 14px 34px rgba(255, 131, 25, 0.42);
    }
`

const QRContainer = styled.div`
    position: relative;
    z-index: 1;
    background: #fff;
    padding: 10px;
    border-radius: 14px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
`

const QRScannerFrame = styled.div`
    position: relative;
    padding: 8px;
    background: #fff;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;

    .corner {
        position: absolute;
        width: 16px;
        height: 16px;
        border: 2.5px solid var(--themeColor);
        pointer-events: none;
        z-index: 2;
    }

    .top-left {
        top: 0;
        left: 0;
        border-right: none;
        border-bottom: none;
        border-top-left-radius: 8px;
    }

    .top-right {
        top: 0;
        right: 0;
        border-left: none;
        border-bottom: none;
        border-top-right-radius: 8px;
    }

    .bottom-left {
        bottom: 0;
        left: 0;
        border-right: none;
        border-top: none;
        border-bottom-left-radius: 8px;
    }

    .bottom-right {
        bottom: 0;
        right: 0;
        border-left: none;
        border-top: none;
        border-bottom-right-radius: 8px;
    }

    .scan-line {
        position: absolute;
        left: 10px;
        right: 10px;
        height: 2px;
        border-radius: 2px;
        background: linear-gradient(90deg, transparent, var(--themeColor), transparent);
        box-shadow: 0 0 8px rgba(255, 131, 25, 0.55);
        animation: qrScan 2.4s ease-in-out infinite;
        z-index: 3;
        pointer-events: none;
    }

    @keyframes qrScan {
        0% { top: 10px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { top: calc(100% - 12px); opacity: 0; }
    }
`

const QRCodeImageWrapper = styled.div`
    position: relative;
    width: 132px;
    height: 132px;
    display: flex;
    align-items: center;
    justify-content: center;

    > img {
        width: 100%;
        height: 100%;
        display: block;
        border-radius: 4px;
        object-fit: contain;
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
    }
`

const QRLogoOverlay = styled.div`
    position: absolute;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    width: 24px;
    height: 24px;
    background: #fff;
    border-radius: 5px;
    padding: 2px;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`

const QRTextBlock = styled.div`
    position: relative;
    z-index: 1;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 200px;
`

const QRText = styled(TextStyled)`
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #fff !important;
    line-height: 1.3;
`

const QRHint = styled(TextStyled)`
    margin: 0;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.88) !important;
    line-height: 1.4;
`

export default AppDownloadSection
